import { SetState } from '@/features/remotion-editor/editor/context-provider';
import {
  CaptionAsset,
  isCaptionAsset,
} from '@/features/remotion-editor/editor/assets/assets';
import {
  CaptionsItem,
  isCaptionsItem,
} from '@/features/remotion-editor/editor/items/captions/captions-item-type';
import { generateRandomId } from '@/features/remotion-editor/editor/utils/generate-random-id';
import {
  safeMs,
  cloneCaptions,
  sortCaptionsByStart,
  getCaptionRange,
  createCaptionClipId,
  updateTrackItems,
} from './helpers';
import { calculateCaptionAssetSize } from './asset-utils';
import { withoutCaptionTranslation } from '@/features/remotion-editor/editor/state/actions/caption-translation-actions';

/**
 * splitCaptionItem의 결과로 생성된 새 아이템 ID를 반환하는 버전
 */
export const splitCaptionItemWithResult = ({
  itemId,
  splitIndex,
  trackIndex,
  itemIndex,
  setState,
}: {
  itemId: string;
  splitIndex: number;
  trackIndex: number;
  itemIndex: number;
  setState: SetState;
}): { success: boolean; newItemId: string | null } => {
  let result = { success: false, newItemId: null as string | null };

  setState({
    update: (prev) => {
      const { tracks, items, assets, fps } = prev.undoableState;
      const targetTrack = tracks[trackIndex];
      const currentItem = items[itemId];

      if (!targetTrack || !currentItem || !isCaptionsItem(currentItem)) {
        return prev;
      }

      const baseAsset = assets[currentItem.assetId];
      if (!isCaptionAsset(baseAsset)) {
        return prev;
      }

      const orderedCaptions = sortCaptionsByStart(
        cloneCaptions(baseAsset.captions),
      );

      if (
        orderedCaptions.length < 2 ||
        splitIndex <= 0 ||
        splitIndex >= orderedCaptions.length
      ) {
        return prev;
      }

      const prefixCaptions = orderedCaptions.slice(0, splitIndex);
      const suffixCaptions = orderedCaptions.slice(splitIndex);

      if (!prefixCaptions.length || !suffixCaptions.length) {
        return prev;
      }

      const prefixRange = getCaptionRange(prefixCaptions);
      const suffixRange = getCaptionRange(suffixCaptions);
      const baseVideoStart = safeMs(baseAsset.videoStart);
      const suffixOffsetMs = Math.max(0, suffixRange.start - baseVideoStart);
      const safeFps = fps > 0 ? fps : 30;

      const originalFrom = currentItem.from;
      const originalDuration = Math.max(1, currentItem.durationInFrames);
      const originalEndFrame = originalFrom + originalDuration;
      const suffixOffsetFrames = Math.round((suffixOffsetMs / 1000) * safeFps);
      const desiredSuffixFrom = originalFrom + suffixOffsetFrames;
      const safeSuffixFrom = Math.min(
        Math.max(desiredSuffixFrom, originalFrom + 1),
        originalEndFrame - 1,
      );

      const prefixFrom = originalFrom;
      const suffixFrom = safeSuffixFrom;

      let prefixDurationFrames = Math.max(1, suffixFrom - prefixFrom);
      let suffixDurationFrames = Math.max(1, originalEndFrame - suffixFrom);
      const combinedFrames = prefixDurationFrames + suffixDurationFrames;
      if (combinedFrames !== originalDuration) {
        const diff = originalDuration - combinedFrames;
        suffixDurationFrames = Math.max(1, suffixDurationFrames + diff);
        prefixDurationFrames = Math.max(
          1,
          originalDuration - suffixDurationFrames,
        );
      }

      const prefixAsset: CaptionAsset = {
        ...withoutCaptionTranslation(baseAsset),
        captions: prefixCaptions,
        size: calculateCaptionAssetSize(prefixCaptions),
        videoStart: baseVideoStart,
        videoEnd: Math.max(baseVideoStart, prefixRange.end),
      };

      const suffixAssetId = `caption-c${generateRandomId()}`;
      const suffixAsset: CaptionAsset = {
        ...withoutCaptionTranslation(baseAsset),
        id: suffixAssetId,
        captions: suffixCaptions,
        size: calculateCaptionAssetSize(suffixCaptions),
        clipId: createCaptionClipId(),
        filename: `${suffixAssetId}.json`,
        remoteUrl: null,
        remoteFileKey: null,
        videoStart: suffixRange.start,
        videoEnd: Math.max(suffixRange.start, suffixRange.end),
      };

      const updatedCurrentItem: CaptionsItem = {
        ...currentItem,
        from: prefixFrom,
        durationInFrames: prefixDurationFrames,
        captionStartInSeconds: prefixAsset?.videoStart
          ? prefixAsset.videoStart / 1000
          : 0,
      };

      const newItemId = `caption-c${generateRandomId()}`;
      const newItem: CaptionsItem = {
        ...currentItem,
        id: newItemId,
        assetId: suffixAsset.id,
        from: suffixFrom,
        durationInFrames: suffixDurationFrames,
        captionStartInSeconds: suffixAsset?.videoStart
          ? suffixAsset.videoStart / 1000
          : 0,
      };

      const updatedTrackItems = updateTrackItems(
        tracks,
        trackIndex,
        (existing) => {
          const next = [...existing];
          next.splice(itemIndex + 1, 0, newItemId);
          return next;
        },
      );

      result = { success: true, newItemId };

      return {
        ...prev,
        undoableState: {
          ...prev.undoableState,
          tracks: updatedTrackItems,
          items: {
            ...prev.undoableState.items,
            [currentItem.id]: updatedCurrentItem,
            [newItemId]: newItem,
          },
          assets: {
            ...prev.undoableState.assets,
            [prefixAsset.id]: prefixAsset,
            [suffixAsset.id]: suffixAsset,
          },
        },
      };
    },
    commitToUndoStack: false, // 삭제와 함께 하나의 undo로 처리하기 위해
  });

  return result;
};

/**
 * captions 아이템을 지정 index에서 두 개로 분할한다.
 * @param itemId 분할할 item id
 * @param splitIndex captions 배열에서 분할할 지점
 * @param trackIndex captions track index
 * @param itemIndex track 내 item index
 * @param setState 전역 상태 업데이트 함수
 */
export const splitCaptionItem = ({
  itemId,
  splitIndex,
  trackIndex,
  itemIndex,
  setState,
}: {
  itemId: string;
  splitIndex: number;
  trackIndex: number;
  itemIndex: number;
  setState: SetState;
}) => {
  setState({
    update: (prev) => {
      const { tracks, items, assets, fps } = prev.undoableState;
      const targetTrack = tracks[trackIndex];
      const currentItem = items[itemId];

      if (!targetTrack || !currentItem || !isCaptionsItem(currentItem)) {
        return prev;
      }

      const baseAsset = assets[currentItem.assetId];
      if (!isCaptionAsset(baseAsset)) {
        return prev;
      }

      const orderedCaptions = sortCaptionsByStart(
        cloneCaptions(baseAsset.captions),
      );

      if (
        orderedCaptions.length < 2 ||
        splitIndex <= 0 ||
        splitIndex >= orderedCaptions.length
      ) {
        return prev;
      }

      // 1) splitIndex 기준으로 caption 배열을 앞/뒤로 나눈다.
      const prefixCaptions = orderedCaptions.slice(0, splitIndex);
      const suffixCaptions = orderedCaptions.slice(splitIndex);

      if (!prefixCaptions.length || !suffixCaptions.length) {
        return prev;
      }

      // 2) 각 구간의 ms 범위를 구한 뒤, 원본 videoStart 기준으로 얼마나 이동했는지 계산한다.
      const prefixRange = getCaptionRange(prefixCaptions);
      const suffixRange = getCaptionRange(suffixCaptions);
      const baseVideoStart = safeMs(baseAsset.videoStart);
      const suffixOffsetMs = Math.max(0, suffixRange.start - baseVideoStart);
      const safeFps = fps > 0 ? fps : 30;

      const originalFrom = currentItem.from;
      const originalDuration = Math.max(1, currentItem.durationInFrames);
      const originalEndFrame = originalFrom + originalDuration;
      // 3) suffixOffsetMs 는 원본 clip에서 분기점까지의 ms 이므로 timeline frame 으로 변환해 위치를 계산한다.
      const suffixOffsetFrames = Math.round((suffixOffsetMs / 1000) * safeFps);
      const desiredSuffixFrom = originalFrom + suffixOffsetFrames;
      const safeSuffixFrom = Math.min(
        Math.max(desiredSuffixFrom, originalFrom + 1),
        originalEndFrame - 1,
      );

      const prefixFrom = originalFrom;
      const suffixFrom = safeSuffixFrom;

      // 4) 두 구간 길이를 프레임 단위로 다시 계산하고, rounding 오차가 있으면 보정한다.
      let prefixDurationFrames = Math.max(1, suffixFrom - prefixFrom);
      let suffixDurationFrames = Math.max(1, originalEndFrame - suffixFrom);
      const combinedFrames = prefixDurationFrames + suffixDurationFrames;
      if (combinedFrames !== originalDuration) {
        const diff = originalDuration - combinedFrames;
        suffixDurationFrames = Math.max(1, suffixDurationFrames + diff);
        prefixDurationFrames = Math.max(
          1,
          originalDuration - suffixDurationFrames,
        );
      }

      // 5) prefix/suffix asset 메타를 각각 갱신해 clip, video 범위를 최신화한다.
      const prefixAsset: CaptionAsset = {
        ...withoutCaptionTranslation(baseAsset),
        captions: prefixCaptions,
        size: calculateCaptionAssetSize(prefixCaptions),
        videoStart: baseVideoStart,
        videoEnd: Math.max(baseVideoStart, prefixRange.end),
      };

      const suffixAssetId = `caption-c${generateRandomId()}`;
      const suffixAsset: CaptionAsset = {
        ...withoutCaptionTranslation(baseAsset),
        id: suffixAssetId,
        captions: suffixCaptions,
        size: calculateCaptionAssetSize(suffixCaptions),
        clipId: createCaptionClipId(),
        filename: `${suffixAssetId}.json`,
        remoteUrl: null,
        remoteFileKey: null,
        videoStart: suffixRange.start,
        videoEnd: Math.max(suffixRange.start, suffixRange.end),
      };

      // 6) 기존 item 은 prefix portion 으로 덮어쓰고, suffix portion 은 새 item 으로 복제한다.
      const updatedCurrentItem: CaptionsItem = {
        ...currentItem,
        from: prefixFrom,
        durationInFrames: prefixDurationFrames,
        captionStartInSeconds: prefixAsset?.videoStart
          ? prefixAsset.videoStart / 1000
          : 0,
      };

      const newItemId = `caption-c${generateRandomId()}`;
      const newItem: CaptionsItem = {
        ...currentItem,
        id: newItemId,
        assetId: suffixAsset.id,
        from: suffixFrom,
        durationInFrames: suffixDurationFrames,
        captionStartInSeconds: suffixAsset?.videoStart
          ? suffixAsset.videoStart / 1000
          : 0,
      };

      // 7) track 배열에는 현재 위치 뒤에 새 item id 를 삽입한다.
      const updatedTrackItems = updateTrackItems(
        tracks,
        trackIndex,
        (existing) => {
          const next = [...existing];
          next.splice(itemIndex + 1, 0, newItemId);
          return next;
        },
      );

      return {
        ...prev,
        undoableState: {
          ...prev.undoableState,
          tracks: updatedTrackItems,
          items: {
            ...prev.undoableState.items,
            [currentItem.id]: updatedCurrentItem,
            [newItemId]: newItem,
          },
          assets: {
            ...prev.undoableState.assets,
            [prefixAsset.id]: prefixAsset,
            [suffixAsset.id]: suffixAsset,
          },
        },
      };
    },
    commitToUndoStack: true,
  });
};
